// Map performance bench: a synthetic board and scripted play sequences for
// app/(preview)/preview/map-bench.tsx, plus frame statistics. Drives the real
// MapView the way a turn does (select a source, pick a target, attack,
// conquer, fortify, clear) so frame costs can be measured on a device and
// compared between builds. See docs/map-perf.md → "Map bench".
import type { GameEvent, MapDef } from '@warcarp/api-types'
import type { HydratedGameState } from './gameState'

export const BENCH_PLAYERS = ['1', '2', '3', '4']

/** A 4-player attack-phase board on `map`: owners round-robin, 2–9 armies, viewer (player 1) to move. */
export function buildBenchState(map: MapDef, now = Date.now()): HydratedGameState {
  const territories = map.territories.map((t, i) => ({
    ...t,
    ownerId: BENCH_PLAYERS[i % BENCH_PLAYERS.length],
    armies: 2 + ((i * 7) % 8),
  }))
  return {
    mapId: map.id,
    map: { ...map, territories },
    players: BENCH_PLAYERS,
    turnIndex: 0,
    phase: 'attack',
    events: [{ kind: 'turn-start', player: '1', turnNumber: 3, ts: now - 1000 }],
  } as unknown as HydratedGameState
}

/**
 * Viewer-owned attack sources with at least one enemy neighbour, each with its
 * enemy neighbours, in map order. Cycling through them spreads the work over
 * the board instead of repainting one corner.
 */
export function attackPairs(state: HydratedGameState): Array<{ from: string; targets: string[] }> {
  const owner = new Map(state.map.territories.map((t) => [t.id, t.ownerId]))
  return state.map.territories
    .filter((t) => t.ownerId === '1')
    .map((t) => ({ from: t.id, targets: (t.adjacencies ?? []).filter((id) => owner.has(id) && owner.get(id) !== '1') }))
    .filter((pair) => pair.targets.length > 0)
}

export type BenchScenario = 'selection' | 'attacks' | 'conquest' | 'idle'
export const BENCH_SCENARIOS: BenchScenario[] = ['selection', 'attacks', 'conquest', 'idle']

/** What the bench screen applies at each step. */
export type BenchAction =
  | { type: 'select'; from: string; targets: string[] }
  | { type: 'target'; to: string }
  | { type: 'clear' }
  | { type: 'events'; events: GameEvent[]; ownerChange?: { territoryId: string; ownerId: string; armies: number } }

export interface BenchStep { at: number; action: BenchAction }

const roll = { attacker: [6, 4, 2], defender: [5, 3] }

/**
 * The scripted timeline (ms from start) for a scenario, cycling through
 * `pairs`. Timings follow play: a player looks for ~600ms between taps, and
 * event animations run ~1.3s.
 *   selection — select a source, pick a target, clear; `reps` times.
 *   attacks   — one source and target, then `reps` non-conquering attacks.
 *   conquest  — select, target, conquering attack + conquest (the territory
 *               changes owner), a fortify, clear; `reps` times.
 *   idle      — nothing for 5s: the floor (static board, frame callback only).
 */
export function benchTimeline(scenario: BenchScenario, pairs: Array<{ from: string; targets: string[] }>, reps: number, startTs: number): BenchStep[] {
  const steps: BenchStep[] = []
  if (scenario === 'idle' || pairs.length === 0) return [{ at: 5000, action: { type: 'clear' } }]
  const attack = (from: string, to: string, at: number, conquered: boolean): GameEvent => ({
    kind: 'attack', attacker: '1', defender: '2', fromTerritoryId: from, toTerritoryId: to,
    roll: roll as never, attackerLost: conquered ? 0 : 1, defenderLost: conquered ? 2 : 1, conquered, ts: startTs + at,
  } as GameEvent)
  const first = pairs[0]!
  let t = 500
  for (let i = 0; i < reps; i++) {
    const { from, targets } = pairs[i % pairs.length]!
    const to = targets[0]!
    if (scenario === 'selection') {
      steps.push({ at: t, action: { type: 'select', from, targets } })
      steps.push({ at: (t += 600), action: { type: 'target', to } })
      steps.push({ at: (t += 600), action: { type: 'clear' } })
      t += 400
    } else if (scenario === 'attacks') {
      if (i === 0) {
        steps.push({ at: t, action: { type: 'select', from, targets } })
        steps.push({ at: (t += 600), action: { type: 'target', to } })
      }
      steps.push({ at: (t += 800), action: { type: 'events', events: [attack(first.from, first.targets[0]!, t, false)] } })
    } else {
      steps.push({ at: t, action: { type: 'select', from, targets } })
      steps.push({ at: (t += 600), action: { type: 'target', to } })
      steps.push({ at: (t += 700), action: { type: 'events', events: [
        attack(from, to, t, true),
        { kind: 'conquest', attacker: '1', defender: '2', territoryId: to, armiesMoved: 3, ts: startTs + t } as GameEvent,
      ], ownerChange: { territoryId: to, ownerId: '1', armies: 3 } } })
      steps.push({ at: (t += 1400), action: { type: 'events', events: [
        { kind: 'fortify', player: '1', fromTerritoryId: from, toTerritoryId: to, armies: 1, ts: startTs + t } as GameEvent,
      ] } })
      steps.push({ at: (t += 1400), action: { type: 'clear' } })
      t += 400
    }
  }
  steps.push({ at: t + 1500, action: { type: 'clear' } })
  return steps
}

export interface FrameStats {
  frames: number
  meanMs: number
  p50Ms: number
  p95Ms: number
  p99Ms: number
  worstMs: number
  over33: number
  over50: number
  over100: number
}

/** Frame-time statistics from inter-frame deltas (ms). */
export function frameStats(deltas: readonly number[]): FrameStats {
  const sorted = [...deltas].filter((d) => d > 0).sort((a, b) => a - b)
  const pick = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))] ?? 0
  const sum = sorted.reduce((a, b) => a + b, 0)
  const round = (n: number) => Math.round(n * 10) / 10
  return {
    frames: sorted.length,
    meanMs: round(sorted.length ? sum / sorted.length : 0),
    p50Ms: round(pick(0.5)),
    p95Ms: round(pick(0.95)),
    p99Ms: round(pick(0.99)),
    worstMs: round(sorted.at(-1) ?? 0),
    over33: sorted.filter((d) => d > 33.4).length,
    over50: sorted.filter((d) => d > 50).length,
    over100: sorted.filter((d) => d > 100).length,
  }
}
